/**
 * What a creator reads when Create or Iterate goes wrong. Server failures carry pipeline
 * detail (module ids, proof traces, provider errors, cut to a storage limit);
 * they stay in the run for admins and logs, and creators get these words.
 */
export const OFFLINE_MESSAGE = 'Your device appears offline. You can keep editing. Analysis and building need a connection. Reconnect, then choose the action again.';
export const ANALYSIS_UNCONFIRMED_MESSAGE = 'We couldn’t confirm that analysis started. Your idea is saved, so you can start again.';
const ANALYSIS_FAILED_MESSAGE = 'We couldn’t shape your idea this time. Your idea is saved, so you can start again.';
const QUOTA_MESSAGE = 'You’ve reached today’s limit for new game ideas. It resets at 00:00 UTC.';

/** Iterate's refused calls: what happened and the one thing to do next. */
export const REVISION_START_FAILED_MESSAGE = 'We couldn’t start this change. Your live game is unchanged. Please try again.';
export const BUILD_REPAIR_FAILED_MESSAGE = 'We couldn’t repair the saved build. Your live game is unchanged. Please try again.';

/** A change that stopped: the live game is untouched, and only a new change can follow. */
export const CHANGE_FAILED_MESSAGE = 'We couldn’t make this change. Your live game is unchanged.';
export const CHANGE_BUSY_MESSAGE = 'We’re too busy to make this change right now. Your live game is unchanged.';

export const RETRY_LABEL = 'Try again';
export const RETRY_LIMIT_CHANGE_MESSAGE = 'We couldn’t finish this change after a few tries. Your live game is unchanged. Please start a new change.';

const isQuotaMessage = (message: string) => /daily concept-analysis limit/i.test(message);

export function plainAnalysisError(raw: string): string {
  if (raw === OFFLINE_MESSAGE || raw === ANALYSIS_UNCONFIRMED_MESSAGE) return raw;
  return isQuotaMessage(raw) ? QUOTA_MESSAGE : ANALYSIS_FAILED_MESSAGE;
}

/** A composed run's failure, named by the stage it stopped on. */
export function plainComposedFailure(stageLabel: string | undefined): string {
  return `${stageLabel ? `We couldn’t finish this step: ${stageLabel}.` : 'We couldn’t finish this step of your game.'} Your finished parts are saved.`;
}

/** A thrown error's words for the creator. Errors without a code are this
 * page's own copy. Coded ones come from the server, where only a rejected
 * title or concept (`inputRejection`) is written for the person who typed it. */
export function plainThrownError(error: unknown, fallback: string, options: { inputRejection?: boolean } = {}): string {
  const message = error instanceof Error ? error.message : '';
  const raw = (error as { code?: unknown } | null)?.code;
  if (typeof raw !== 'string') return message || fallback;
  if (isQuotaMessage(message)) return QUOTA_MESSAGE;
  const code = raw.replace(/^functions\//, '');
  return options.inputRejection && (code === 'invalid-argument' || code === 'validation') && message ? message : fallback;
}

const INTERNAL_NAME = /\b(?:rule|ai)\.[A-Za-z]\w*(?:\.\w+)*\b|\b[a-z]\w*(?:\.\w+)*\.[a-z]+[A-Z]\w*\b/g;

/** Model-written plan text can quote internal names such as rule.contactDamage.
 * Say the last part in words: "contact damage". */
export function plainPlanText(text: string): string {
  return text.replace(INTERNAL_NAME, name => name.slice(name.lastIndexOf('.') + 1)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase());
}
